/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { memo, useCallback, useState, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import DropdownMenu, { type CustomTriggerProps } from '@atlaskit/dropdown-menu';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { NAVIGATION_ITEMS_PAGE_SIZE } from '@atlassian/jira-navigation-apps-resources/src/constants.tsx';
import type { TabsDescriptor } from '@atlassian/jira-horizontal-nav-common/src/common/types.tsx';
import { useIsIssuePreviewPanelOpen } from '@atlassian/jira-preview-panels/src/useIsIssuePreviewPanelOpen.tsx';
import { useViewMode } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { CONTENT_MIN_WIDTH } from '../common/constants.tsx';
import type { JiraNavigationItemTypeKey } from './content/__generated__/AddTabContentQuery.graphql';
import type { AddTab_container$key } from './__generated__/AddTab_container.graphql';
import AddTabTrigger from './AddTabTrigger.tsx';
import { LoadingSkeleton } from './content/loading-skeleton/index.tsx';
import { addTabContentEntryPoint } from './content/entrypoint.tsx';

type Props = {
	containerFragment: AddTab_container$key;
	tabsDescriptor: TabsDescriptor;
	onAddItem: (item: { id: string; typeKey: JiraNavigationItemTypeKey }) => void;
};

const AddTab = ({ containerFragment, tabsDescriptor, onAddItem }: Props): React.JSX.Element => {
	const containerData = useFragment(
		graphql`
			fragment AddTab_container on JiraContainerNavigationResult {
				... on JiraContainerNavigation {
					scopeId @required(action: THROW)
				}
			}
		`,
		containerFragment,
	);

	const scopeId = containerData.scopeId ?? '';

	const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState<boolean>(false);
	const onOpenChange = useCallback(() => {
		setIsDropdownMenuOpen((isDropdownMenuCurrent) => !isDropdownMenuCurrent);
	}, []);

	const closeMenu = useCallback(() => {
		setIsDropdownMenuOpen(false);
	}, []);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		addTabContentEntryPoint,
		useMemo(() => ({ scopeId, first: NAVIGATION_ITEMS_PAGE_SIZE }), [scopeId]),
	);

	const triggerRef = useEntryPointButtonTrigger(entryPointActions, !isDropdownMenuOpen);

	const trigger = ({
		isSelected: isMenuOpen,
		onClick,
		triggerRef: triggerRefProp,
		...triggerProps
	}: CustomTriggerProps) => (
		<AddTabTrigger
			onClick={(event) => {
				onClick && onClick(event);
			}}
			isSelected={isDropdownMenuOpen}
			{...triggerProps}
			ref={(element) => {
				triggerRef(element);
				typeof triggerRefProp === 'function' && triggerRefProp(element);
			}}
			testId="navigation-kit-add-tab.ui.trigger"
		/>
	);

	const runtimeProps = useMemo(
		() => ({
			onAddItem,
			onClose: closeMenu,
			tabsDescriptor,
		}),
		[closeMenu, onAddItem, tabsDescriptor],
	);

	let isPreviewPanelOpen = false;
	let isSidebarView = false;
	if (fg('fix_add_nav_popup_behind_preview_panel')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isPreviewPanelOpen = useIsIssuePreviewPanelOpen();

		// eslint-disable-next-line react-hooks/rules-of-hooks
		isSidebarView = useViewMode() === 'SIDEBAR';
	}

	return (
		<DropdownMenu
			trigger={trigger}
			placement={
				isPreviewPanelOpen && isSidebarView && fg('fix_add_nav_popup_behind_preview_panel')
					? 'bottom-end'
					: 'bottom-start'
			}
			testId="navigation-kit-add-tab.ui.dropdown-menu"
			isOpen={isDropdownMenuOpen}
			onOpenChange={onOpenChange}
			shouldRenderToParent={fg('should-render-to-parent-should-be-true-navigation')}
		>
			<Box xcss={addTabContentWrapperStyles}>
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="navigation-kit-add-tab-content"
					packageName="jiraNavigationKitAddTab"
					runtimeProps={runtimeProps}
					fallback={<LoadingSkeleton />}
				/>
			</Box>
		</DropdownMenu>
	);
};

export default memo(AddTab);

const addTabContentWrapperStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	inlineSize: `${CONTENT_MIN_WIDTH}px`,
});
